import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { toast } from "react-toastify";
import styles from "./RenewalReminders.module.css";

// ─── Constants ──────────────────────────────────────────────────────────
const HORIZON_OPTIONS = [
  { value: 7, label: "Next 7 days" },
  { value: 14, label: "Next 14 days" },
  { value: 30, label: "Next 30 days" },
  { value: 60, label: "Next 60 days" },
  { value: 90, label: "Next 90 days" },
];

// Bucket order is also the visual order in chips / chart.
const BUCKETS = [
  { key: "expiring_3", label: "≤ 3 days", color: "#e74c3c", danger: true },
  { key: "expiring_5", label: "4 - 5 days", color: "#f39c12" },
  { key: "expiring_10", label: "6 - 10 days", color: "#1c9aea" },
  { key: "expiring_30_plus", label: "10+ days", color: "#607084" },
];

const INTERVAL_FILTERS = [
  { value: "all", label: "All intervals" },
  { value: "yearly", label: "Yearly only" },
  { value: "monthly", label: "Monthly only" },
];

// ─── Helpers ────────────────────────────────────────────────────────────
function formatMoney(amount, currency) {
  const num = Number(amount) || 0;
  // Currency comes in as "inr"/"usd"/etc from the DB; ISO upper case for
  // Intl. Fall back to plain locale-grouped number if Intl rejects it.
  const code = String(currency || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `${code} ${num.toLocaleString("en-IN")}`;
  }
}

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

function daysPillClass(daysLeft) {
  if (daysLeft <= 3) return styles.daysPill3;
  if (daysLeft <= 5) return styles.daysPill5;
  if (daysLeft <= 10) return styles.daysPill10;
  return styles.daysPill30;
}

// Sum money across rows whose currency may differ. We bucket totals per
// currency to avoid the "₹100 + $1 = 101" lie. UI displays each currency
// total on its own line.
function sumByCurrency(rows, key = "renewal_amount") {
  const map = new Map();
  for (const r of rows) {
    const c = String(r.currency || "INR").toUpperCase();
    map.set(c, (map.get(c) || 0) + (Number(r[key]) || 0));
  }
  return Array.from(map.entries()).map(([currency, amount]) => ({
    currency,
    amount,
  }));
}

function formatMoneyList(entries) {
  if (!entries.length) return "—";
  return entries.map((e) => formatMoney(e.amount, e.currency)).join(" · ");
}

// ─── Component ──────────────────────────────────────────────────────────
const RenewalReminders = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [horizonDays, setHorizonDays] = useState(30);

  // Local filter state — applied client-side over the loaded items array.
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState(null); // null = all
  const [intervalFilter, setIntervalFilter] = useState("all");

  const fetchData = async (horizonOverride) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/renewal_reminders`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            horizonDays: horizonOverride ?? horizonDays,
          }),
        },
      );
      const json = await res.json();
      if (json?.status === 200) {
        setData(json.data);
      } else {
        toast.error(json?.message || "Failed to load renewal reminders");
      }
    } catch (err) {
      console.error("[renewal_reminders]", err);
      toast.error("Failed to load renewal reminders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(horizonDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizonDays]);

  // ─── Derived ──────────────────────────────────────────────────────────
  const summary = data?.summary || null;
  const items = data?.items || [];

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (bucketFilter && it.bucket !== bucketFilter) return false;
      if (intervalFilter === "yearly" && !it.is_yearly) return false;
      if (intervalFilter === "monthly" && it.is_yearly) return false;
      if (q) {
        const hay = [
          it.workspace_name,
          it.owner_email,
          it.owner_name,
          it.marketplace_name,
          it.sku_name,
          it.domain,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, bucketFilter, intervalFilter]);

  const filteredAmountByCurrency = useMemo(
    () => sumByCurrency(filteredItems),
    [filteredItems],
  );

  // ─── Charts ───────────────────────────────────────────────────────────
  const donutOptions = useMemo(
    () => ({
      chart: { type: "donut", fontFamily: "League Spartan, sans-serif" },
      labels: BUCKETS.map((b) => b.label),
      colors: BUCKETS.map((b) => b.color),
      legend: { position: "bottom", fontSize: "12px" },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: "68%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total",
                fontSize: "12px",
                color: "#607084",
                formatter: () => String(summary?.total_count ?? 0),
              },
              value: {
                fontSize: "26px",
                fontWeight: 800,
                color: "#1f2c3c",
              },
            },
          },
        },
      },
      stroke: { width: 0 },
      tooltip: {
        y: { formatter: (v) => `${v} renewal${v === 1 ? "" : "s"}` },
      },
    }),
    [summary],
  );

  const donutSeries = useMemo(() => {
    if (!summary) return BUCKETS.map(() => 0);
    return BUCKETS.map((b) => summary.buckets?.[b.key] ?? 0);
  }, [summary]);

  const timelineOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        fontFamily: "League Spartan, sans-serif",
        toolbar: { show: false },
        stacked: true,
      },
      colors: ["#1c9aea", "#cfe1f2"],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "70%",
        },
      },
      dataLabels: { enabled: false },
      legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
      grid: { borderColor: "#eef3f8" },
      xaxis: {
        type: "datetime",
        labels: { style: { colors: "#8696aa", fontSize: "11px" } },
      },
      yaxis: {
        labels: {
          style: { colors: "#8696aa", fontSize: "11px" },
          formatter: (v) => Math.round(v),
        },
        title: {
          text: "Renewals per day",
          style: { color: "#8696aa", fontSize: "11px", fontWeight: 600 },
        },
      },
      tooltip: {
        x: { format: "dd MMM yyyy" },
      },
    }),
    [],
  );

  const timelineSeries = useMemo(() => {
    const tl = data?.timeline || [];
    return [
      {
        name: "Yearly",
        data: tl.map((t) => ({ x: t.date, y: t.yearly_count })),
      },
      {
        name: "Monthly + other",
        data: tl.map((t) => ({ x: t.date, y: t.count - t.yearly_count })),
      },
    ];
  }, [data]);

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Renewal Reminders</h1>
          <div className={styles.subtitle}>
            Upcoming subscription renewals across all customers. Yearly
            renewals are flagged for priority review — annual prices drift
            more between cycles than monthly.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 170, background: "#fff", borderRadius: "999px" }}>
            <InputLabel id="horizon-label">Horizon</InputLabel>
            <Select
              labelId="horizon-label"
              label="Horizon"
              value={horizonDays}
              onChange={(e) => setHorizonDays(Number(e.target.value))}
              sx={{ borderRadius: "999px" }}
            >
              {HORIZON_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            className={styles.refreshBtn}
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => fetchData()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loaderWrap}>
          <CircularProgress />
        </div>
      ) : !summary ? (
        <div className={styles.emptyState}>No renewal data available.</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className={styles.summaryRow}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>Total Renewals</div>
              <div className={styles.summaryValue}>{summary.total_count}</div>
              <div className={styles.summaryHint}>
                across {data?.by_workspace?.length || 0} workspace
                {(data?.by_workspace?.length || 0) === 1 ? "" : "s"} · next{" "}
                {data?.horizon_days || horizonDays} days
              </div>
            </div>
            <div className={`${styles.summaryCard} ${styles.summaryCardHighlight}`}>
              <div className={styles.summaryLabel}>Yearly Renewals (High Priority)</div>
              <div className={styles.summaryValue}>{summary.yearly_count}</div>
              <div className={styles.summaryHint}>
                {formatMoneyList(
                  sumByCurrency(
                    items.filter((it) => it.is_yearly),
                    "renewal_amount",
                  ),
                )}
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>Monthly Renewals</div>
              <div className={styles.summaryValue}>{summary.monthly_count}</div>
              <div className={styles.summaryHint}>
                {formatMoneyList(
                  sumByCurrency(
                    items.filter((it) => !it.is_yearly),
                    "renewal_amount",
                  ),
                )}
              </div>
            </div>
            <div
              className={`${styles.summaryCard} ${
                summary.buckets.expiring_3 > 0 ? styles.summaryCardDanger : ""
              }`}
            >
              <div className={styles.summaryLabel}>Expiring in ≤ 3 days</div>
              <div className={styles.summaryValue}>{summary.buckets.expiring_3}</div>
              <div className={styles.summaryHint}>
                {summary.buckets.expiring_3 === 0
                  ? "Nothing critical right now"
                  : "Reach out before they lapse"}
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div className={styles.chartsRow}>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Urgency mix</div>
              <div className={styles.chartSub}>
                How many renewals fall into each window
              </div>
              <ReactApexChart
                options={donutOptions}
                series={donutSeries}
                type="donut"
                height={260}
              />
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Renewal timeline</div>
              <div className={styles.chartSub}>
                Daily count over the next {data?.horizon_days || horizonDays}{" "}
                days. Yearly subscriptions in dark blue — give those the most
                attention.
              </div>
              <ReactApexChart
                options={timelineOptions}
                series={timelineSeries}
                type="bar"
                height={260}
              />
            </div>
          </div>

          {/* Filters bar */}
          <div className={styles.filtersBar}>
            <TextField
              className={styles.searchInput}
              size="small"
              placeholder="Search workspace, owner, plan, domain…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 280, flex: "1 1 280px" }}
            />
            {BUCKETS.map((b) => {
              const active = bucketFilter === b.key;
              return (
                <span
                  key={b.key}
                  className={`${styles.bucketChip} ${
                    b.danger ? styles.bucketChipDanger : ""
                  } ${active ? styles.bucketChipActive : ""}`}
                  onClick={() => setBucketFilter(active ? null : b.key)}
                >
                  {b.label}
                  <span className={styles.chipCount}>
                    {summary.buckets[b.key] ?? 0}
                  </span>
                </span>
              );
            })}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="interval-label">Interval</InputLabel>
              <Select
                labelId="interval-label"
                label="Interval"
                value={intervalFilter}
                onChange={(e) => setIntervalFilter(e.target.value)}
              >
                {INTERVAL_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(bucketFilter || intervalFilter !== "all" || search) && (
              <Button
                size="small"
                onClick={() => {
                  setBucketFilter(null);
                  setIntervalFilter("all");
                  setSearch("");
                }}
                sx={{ textTransform: "none", color: "#607084" }}
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* Table */}
          <div className={styles.tableCard}>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Workspace / Owner</th>
                    <th>Plan</th>
                    <th>Interval</th>
                    <th>Due</th>
                    <th>Days Left</th>
                    <th>Vendor Renewal</th>
                    <th>Price Validity</th>
                    <th>Priority</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <div className={styles.emptyState}>
                          No renewals match the current filters.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((it) => (
                      <tr key={it.id}>
                        <td className={styles.workspaceCell}>
                          <strong>{it.workspace_name}</strong>
                          <span className={styles.ownerEmail}>
                            {it.owner_name
                              ? `${it.owner_name} · `
                              : ""}
                            {it.owner_email || "no owner email"}
                          </span>
                        </td>
                        <td className={styles.subCell}>
                          <strong>{it.marketplace_name || "—"}</strong>
                          {it.domain ? (
                            <span className={styles.meta}>{it.domain}</span>
                          ) : it.no_of_licences ? (
                            <span className={styles.meta}>
                              {it.no_of_licences} licence
                              {it.no_of_licences === 1 ? "" : "s"}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <span
                            className={`${styles.intervalPill} ${
                              it.is_yearly ? styles.intervalPillYearly : ""
                            }`}
                          >
                            {it.n_interval} {it.interval}
                          </span>
                        </td>
                        <td>{formatDate(it.due_date)}</td>
                        <td>
                          <span
                            className={`${styles.daysPill} ${daysPillClass(
                              it.days_left,
                            )}`}
                          >
                            {it.days_left === 0
                              ? "today"
                              : `${it.days_left} day${
                                  it.days_left === 1 ? "" : "s"
                                }`}
                          </span>
                        </td>
                        <td>
                          {it.vendor_renewal_date
                            ? formatDate(it.vendor_renewal_date)
                            : "–"}
                        </td>
                        <td>
                          {it.price_validity_expires_at ? (
                            <span
                              style={{
                                display: "inline-flex",
                                flexDirection: "column",
                                lineHeight: 1.2,
                              }}
                            >
                              <span>
                                {formatDate(it.price_validity_expires_at)}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  marginTop: 3,
                                  padding: "1px 7px",
                                  borderRadius: 999,
                                  fontWeight: 700,
                                  alignSelf: "flex-start",
                                  background: it.price_validity_warn
                                    ? "#ffe8e3"
                                    : "#e8f5ef",
                                  color: it.price_validity_warn
                                    ? "#c0392b"
                                    : "#1f8a3a",
                                }}
                                title={
                                  it.price_validity_n && it.price_validity_unit
                                    ? `Validity: ${it.price_validity_n} ${it.price_validity_unit}`
                                    : ""
                                }
                              >
                                {it.price_validity_days_left == null
                                  ? "—"
                                  : it.price_validity_days_left < 0
                                    ? `expired ${Math.abs(
                                        it.price_validity_days_left,
                                      )}d ago`
                                    : it.price_validity_warn
                                      ? `in ${it.price_validity_days_left}d`
                                      : `${it.price_validity_days_left}d left`}
                              </span>
                            </span>
                          ) : (
                            "–"
                          )}
                        </td>
                        <td>
                          <span
                            className={`${styles.priorityFlag} ${
                              it.is_yearly
                                ? styles.priorityHigh
                                : styles.priorityLow
                            }`}
                          >
                            {it.is_yearly ? "High" : "Low"}
                          </span>
                        </td>
                        <td className={styles.amountCell}>
                          {formatMoney(it.renewal_amount, it.currency)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.totalFooter}>
              <span>
                Showing <strong>{filteredItems.length}</strong> of{" "}
                <strong>{items.length}</strong> renewals
              </span>
              <span>
                Filtered total:{" "}
                <strong>{formatMoneyList(filteredAmountByCurrency)}</strong>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RenewalReminders;
